import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Card, Spinner, Form, ProgressBar, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  FaMoneyBillWave, FaChartLine, FaChartPie, FaUniversity,
  FaHandshake, FaCogs, FaUsers, FaTruck, FaTags, FaLayerGroup,
  FaPercentage, FaExchangeAlt, FaArrowRight,
} from "react-icons/fa";
import { toast } from "react-toastify";
import axiosInstance from "../../axiosInstance";
import AdLayout from "../../components/AdLayout";
import ReportSummaryCard from "./components/ReportSummaryCard";
import PeriodCompareLineChart from "./components/PeriodCompareLineChart";
import {
  PERIOD_OPTIONS, buildPeriodParams, buildPreviousPeriodParams, getPeriodLabel,
} from "./utils/periodFilter";
import { formatVND, formatMoney } from "./utils/moneyFormat";
import "./accountingResponsive.css";

const COLORS = ["#0d6efd", "#198754", "#dc3545", "#ffc107", "#6f42c1", "#0dcaf0", "#fd7e14", "#e83e8c"];
const unwrap = (res) => res?.data?.data ?? res?.data ?? {};
const DEFAULT_PERIOD = "MONTH";
const isCanceled = (err) =>
  err?.code === "ERR_CANCELED" || err?.name === "CanceledError" || err?.name === "AbortError";

const fmtShort = (v) => {
  if (v == null) return "—";
  const n = Number(v);
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${(n / 1e9).toFixed(1)} tỷ`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(1)} tr`;
  return formatMoney(n);
};

/** % thay đổi so với kỳ trước; null khi kỳ trước = 0 (không so sánh được). */
const pctDelta = (cur, prev) => {
  const c = Number(cur || 0);
  const p = Number(prev || 0);
  if (!p) return null;
  return ((c - p) / Math.abs(p)) * 100;
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e9ecef",
        borderRadius: 8,
        padding: "10px 14px",
        boxShadow: "0 4px 12px rgba(0,0,0,.1)",
        fontSize: "0.75rem",
      }}
    >
      <div className="fw-bold mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ color: p.color }}>
          {p.name}: {formatVND(p.value)}
        </div>
      ))}
    </div>
  );
};

const TopListCard = ({ title, icon: Icon, iconColor, rows, emptyText, onRowClick, activeId, footerLink }) => (
  <Card className="border-0 h-100" style={{ borderRadius: 14, boxShadow: "0 2px 12px rgba(0,0,0,.07)" }}>
    <Card.Body>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h6 className="fw-bold mb-0">
          {Icon && <Icon className="me-2" style={{ color: iconColor }} />}
          {title}
        </h6>
        {footerLink && (
          <button
            type="button"
            className="btn btn-link btn-sm p-0 text-decoration-none"
            onClick={footerLink}
          >
            Xem báo cáo <FaArrowRight size={11} />
          </button>
        )}
      </div>
      {rows.length > 0 ? (
        <div className="acc-table-wrap">
          <table className="table table-sm table-hover mb-0">
            <thead>
              <tr className="text-muted">
                <th style={{ width: 36 }}>#</th>
                <th>Tên</th>
                <th className="text-end">Tổng tiền</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const clickable = typeof onRowClick === "function";
                const isActive = activeId != null && String(activeId) === String(r.id);
                return (
                  <tr
                    key={r.id ?? `${r.name}-${i}`}
                    onClick={clickable ? () => onRowClick(r) : undefined}
                    style={{
                      cursor: clickable ? "pointer" : "default",
                      background: isActive ? "rgba(13,110,253,.08)" : undefined,
                    }}
                  >
                    <td className="text-muted">{i + 1}</td>
                    <td>
                      <div className="fw-medium text-truncate" style={{ maxWidth: 220 }}>{r.name || "—"}</div>
                      {r.code ? <div className="text-muted acc-kpi-meta">{r.code}</div> : null}
                    </td>
                    <td className="text-end fw-bold" style={{ color: iconColor }}>
                      {formatVND(r.totalAmount)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center text-muted py-4">{emptyText}</div>
      )}
    </Card.Body>
  </Card>
);

const FinancialDashboardBody = ({ embedded = false }) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [monthlyTrend, setMonthlyTrend] = useState([]);
  const [lineReports, setLineReports] = useState([]);
  const [expenseTotals, setExpenseTotals] = useState(null);
  const [topCustomers, setTopCustomers] = useState([]);
  const [topPartners, setTopPartners] = useState([]);
  const [assets, setAssets] = useState(null);
  const [assetsPrev, setAssetsPrev] = useState(null);
  const [cashflow, setCashflow] = useState(null);
  const [cashflowPrev, setCashflowPrev] = useState(null);
  const [profit, setProfit] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);

  // { type: "total" } | { type: "revenue" | "partnerExpense" | "operatingExpense"
  //   | "grossProfit" | "netProfit" | "assets" | "cashflow" }
  // | { type: "line", id, name } | { type: "category", id, name }
  const [chartMetric, setChartMetric] = useState({ type: "total" });
  const [selectedLineId, setSelectedLineId] = useState(null);
  const [scopedTrend, setScopedTrend] = useState([]);
  const [scopedTrendLoading, setScopedTrendLoading] = useState(false);

  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const abortRef = useRef(null);
  const scopedAbortRef = useRef(null);
  const seqRef = useRef(0);

  const periodParams = useMemo(
    () => buildPeriodParams(period, customFrom, customTo),
    [period, customFrom, customTo]
  );
  const periodLabel = getPeriodLabel(period);

  const scopedParams = useMemo(() => {
    const p = { ...periodParams };
    if (selectedLineId) p.lineId = selectedLineId;
    return p;
  }, [periodParams, selectedLineId]);

  const fetchAll = useCallback(async (params) => {
    const seq = ++seqRef.current;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    const signal = controller.signal;
    const prevParams = buildPreviousPeriodParams(params.period, params.dateFrom, params.dateTo);
    if (params.lineId) prevParams.lineId = params.lineId;
    // line-reports luôn hiển thị toàn bộ line (chỉ lọc theo thời gian) để còn click chọn.
    const lineParams = { ...params };
    delete lineParams.lineId;
    try {
      const results = await Promise.allSettled([
        axiosInstance.get("/api/accounting/dashboard/summary", { params, signal }),
        axiosInstance.get("/api/accounting/dashboard/monthly-trend", { params, signal }),
        axiosInstance.get("/api/accounting/dashboard/line-reports", { params: lineParams, signal }),
        axiosInstance.get("/api/accounting/expenses/summary/totals", { params, signal }),
        axiosInstance.get("/api/accounting/dashboard/top-parties", { params: { ...params, limit: 5 }, signal }),
        axiosInstance.get("/api/accounting/assets/report", { params, signal }),
        axiosInstance.get("/api/accounting/assets/report", { params: prevParams, signal }),
        axiosInstance.get("/api/accounting/cash-flows/report", { params, signal }),
        axiosInstance.get("/api/accounting/cash-flows/report", { params: prevParams, signal }),
        axiosInstance.get("/api/accounting/profit/report", { params, signal }),
        axiosInstance.get("/api/accounting/bank-accounts/active", { signal }).catch(() => ({ data: [] })),
      ]);
      if (seq !== seqRef.current) return;
      const canceledAny = results.some(
        (r) => r.status === "rejected" && isCanceled(r.reason)
      );
      if (canceledAny) return;

      const val = (i) => (results[i].status === "fulfilled" ? unwrap(results[i].value) : null);
      setSummary(val(0) || null);
      const mt = val(1);
      setMonthlyTrend(Array.isArray(mt) ? mt : []);
      const lr = val(2);
      setLineReports(Array.isArray(lr) ? lr : []);
      setExpenseTotals(val(3) || null);
      const tp = val(4) || {};
      setTopCustomers(Array.isArray(tp.topCustomers) ? tp.topCustomers : []);
      setTopPartners(Array.isArray(tp.topPartners) ? tp.topPartners : []);
      setAssets(val(5) || null);
      setAssetsPrev(val(6) || null);
      setCashflow(val(7) || null);
      setCashflowPrev(val(8) || null);
      setProfit(val(9) || null);
      const banks = val(10);
      setBankAccounts(Array.isArray(banks) ? banks : []);
    } catch (err) {
      if (isCanceled(err) || seq !== seqRef.current) return;
      toast.error("Không thể tải dữ liệu dashboard");
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (period === "CUSTOM" && (!customFrom || !customTo)) return undefined;
    fetchAll(scopedParams);
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [fetchAll, scopedParams, period, customFrom, customTo]);

  // Biểu đồ scoped cho các chỉ tiêu cần gọi trend riêng.
  useEffect(() => {
    const t = chartMetric.type;
    const needsFetch =
      t === "revenue" || t === "partnerExpense" || t === "operatingExpense" || t === "category";
    if (!needsFetch) return undefined;
    if (scopedAbortRef.current) scopedAbortRef.current.abort();
    const controller = new AbortController();
    scopedAbortRef.current = controller;
    setScopedTrendLoading(true);

    let url = "/api/accounting/expenses/trend";
    const params = { ...scopedParams };
    if (t === "revenue") {
      url = "/api/accounting/revenues/trend";
    } else if (t === "partnerExpense") {
      params.expenseKind = "PARTNER";
    } else if (t === "operatingExpense") {
      params.expenseKind = "OPERATING";
    } else if (t === "category") {
      params.categoryId = chartMetric.id;
    }

    axiosInstance.get(url, { params, signal: controller.signal })
      .then((res) => {
        const body = unwrap(res);
        setScopedTrend(Array.isArray(body) ? body : []);
      })
      .catch((err) => {
        if (isCanceled(err)) return;
        toast.error("Không thể tải biểu đồ");
      })
      .finally(() => setScopedTrendLoading(false));
    return () => controller.abort();
  }, [chartMetric, scopedParams]);

  const selectMetric = (metric) => {
    if (metric.type === "total") {
      setSelectedLineId(null);
      setChartMetric({ type: "total" });
      return;
    }
    if (metric.type === "line") {
      // click lại line đang chọn -> bỏ chọn, về tổng quan.
      if (selectedLineId && String(selectedLineId) === String(metric.id)) {
        setSelectedLineId(null);
        setChartMetric({ type: "total" });
        return;
      }
      setSelectedLineId(metric.id);
      setChartMetric(metric);
      return;
    }
    setChartMetric(metric);
  };

  const handleLineFilterChange = (event) => {
    const value = event.target.value;
    if (!value) {
      setSelectedLineId(null);
      setChartMetric({ type: "total" });
      return;
    }
    const line = lineReports.find((item) => String(item.lineId) === String(value));
    setSelectedLineId(value);
    setChartMetric({
      type: "line",
      id: value,
      name: line?.lineName || `Line ${value}`,
    });
  };

  const goReport = (tab, extra = {}) => {
    const sp = new URLSearchParams({ tab });
    if (period) sp.set("period", period);
    if (periodParams.dateFrom) sp.set("dateFrom", periodParams.dateFrom);
    if (periodParams.dateTo) sp.set("dateTo", periodParams.dateTo);
    if (selectedLineId) sp.set("lineId", String(selectedLineId));
    Object.entries(extra).forEach(([k, v]) => {
      if (v != null && v !== "") sp.set(k, String(v));
    });
    navigate(`/ke-toan/bao-cao?${sp.toString()}`);
  };

  // ─── Derived ────────────────────────────────────────────────────────────
  const totalDebt = (summary?.totalReceivable || 0) + (summary?.totalPayable || 0);
  const receivablePct = totalDebt > 0 ? ((summary?.totalReceivable || 0) / totalDebt) * 100 : 50;

  const revenuePie = useMemo(
    () => lineReports
      .filter((lr) => Number(lr.revenue || 0) > 0)
      .map((lr) => ({ name: lr.lineName || `Line ${lr.lineId}`, value: Number(lr.revenue || 0) })),
    [lineReports]
  );

  const byCategory = useMemo(() => expenseTotals?.byCategory || [], [expenseTotals]);
  const categoryPie = useMemo(
    () => byCategory
      .filter((c) => Number(c.amount || 0) > 0)
      .map((c) => ({
        name: c.categoryName || "Không danh mục",
        value: Number(c.amount || 0),
        sharePct: Number(c.percent || 0),
        id: c.categoryId,
      })),
    [byCategory]
  );

  const topCategories = useMemo(
    () => [...byCategory]
      .filter((c) => Number(c.amount || 0) > 0)
      .sort((a, b) => Number(b.amount || 0) - Number(a.amount || 0))
      .slice(0, 5)
      .map((c) => ({ id: c.categoryId, name: c.categoryName || "Không danh mục", totalAmount: Number(c.amount || 0) })),
    [byCategory]
  );

  const monthlyChartData = useMemo(
    () => monthlyTrend.map((d) => ({
      bucket: d.bucket,
      "Doanh thu": Number(d.revenue || 0),
      "Chi phí": Number(d.expense || 0),
      "Doanh thu kỳ trước": Number(d.prevRevenue || 0),
      "Chi phí kỳ trước": Number(d.prevExpense || 0),
    })),
    [monthlyTrend]
  );

  const assetChartData = useMemo(
    () => (assets?.trend || []).map((d) => ({
      bucket: d.bucket,
      "Tài sản lưu động": Number(d.currentAssets || 0),
      "Tài sản cố định": Number(d.fixedAssets || 0),
      "Tổng tài sản": Number(d.totalAssets || 0),
    })),
    [assets]
  );

  const cashflowChartData = useMemo(
    () => (cashflow?.dailyTrend || []).map((d) => ({
      bucket: d.date,
      "Tiền vào": Number(d.inflow || 0),
      "Tiền ra": Number(d.outflow || 0),
    })),
    [cashflow]
  );

  const netMargin = Number(summary?.netMarginPercent || 0);

  const kpis = useMemo(() => {
    if (!summary) return [];
    return [
      {
        key: "total", title: "Tổng quan Doanh thu", icon: FaChartPie, color: "#764ba2",
        value: summary.revenue, subtitle: "xem tất cả biểu đồ",
      },
      {
        key: "revenue", title: "Doanh thu", icon: FaMoneyBillWave, color: "#198754",
        value: summary.revenue, delta: pctDelta(summary.revenue, summary.prevRevenue),
      },
      {
        key: "partnerExpense", title: "Chi phí NCC", icon: FaHandshake, color: "#dc3545",
        value: summary.partnerExpense, invertDelta: true,
        delta: pctDelta(summary.partnerExpense, summary.prevPartnerExpense),
      },
      {
        key: "operatingExpense", title: "Chi phí vận hành", icon: FaCogs, color: "#fd7e14",
        value: summary.operatingExpense ?? summary.otherExpense, invertDelta: true,
        delta: pctDelta(summary.operatingExpense ?? summary.otherExpense, summary.prevOperatingExpense),
      },
      {
        key: "grossProfit", title: "Lợi nhuận gộp", icon: FaChartLine, color: "#0dcaf0",
        value: summary.grossProfit, delta: pctDelta(summary.grossProfit, summary.prevGrossProfit),
      },
      {
        key: "netProfit", title: "Lợi nhuận ròng", icon: FaChartLine, color: "#0d6efd",
        value: summary.netProfit, delta: pctDelta(summary.netProfit, summary.prevNetProfit),
      },
      {
        key: "netMargin", title: "Biên LN ròng", icon: FaPercentage, color: "#6f42c1",
        value: netMargin, format: "percent", metric: "netProfit",
        delta: pctDelta(summary.netMarginPercent, summary.prevNetMarginPercent),
      },
      {
        key: "assets", title: "Tổng tài sản", icon: FaUniversity, color: "#20c997",
        value: assets?.totalAssets, subtitle: "Toàn công ty",
        delta: pctDelta(assets?.totalAssets, assetsPrev?.totalAssets),
      },
      {
        key: "cashflow", title: "Dòng tiền ròng", icon: FaExchangeAlt, color: "#e83e8c",
        value: cashflow?.netFlow, delta: pctDelta(cashflow?.netFlow, cashflowPrev?.netFlow),
      },
    ];
  }, [summary, assets, assetsPrev, cashflow, cashflowPrev, netMargin]);

  const isMetricActive = (key) => {
    if (key === "total") return chartMetric.type === "total" && !selectedLineId;
    if (key === "netMargin") return chartMetric.type === "netProfit";
    return chartMetric.type === key;
  };

  const pieSliceLabel = ({ percent }) => `${((Number(percent) || 0) * 100).toFixed(0)}%`;
  const pieTooltipFormatter = (v, _n, item) => {
    const pct = item?.payload?.sharePct;
    const pctText = pct != null ? ` (${Number(pct).toFixed(1)}%)` : "";
    return [`${formatVND(v)}${pctText}`, item?.payload?.name || ""];
  };

  const renderPie = (title, data, onSliceClick) => (
    <Card className="border-0 h-100" style={{ borderRadius: 14, boxShadow: "0 2px 12px rgba(0,0,0,.07)" }}>
      <Card.Body>
        <h6 className="fw-bold mb-3">{title}</h6>
        {data.length > 0 ? (
          <div className="acc-chart-wrap">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={82}
                  paddingAngle={2}
                  dataKey="value"
                  label={pieSliceLabel}
                  labelLine={false}
                  onClick={onSliceClick ? (entry) => onSliceClick(entry) : undefined}
                  style={{ cursor: onSliceClick ? "pointer" : "default" }}
                >
                  {data.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={pieTooltipFormatter} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-center text-muted py-5">Chưa có dữ liệu</div>
        )}
      </Card.Body>
    </Card>
  );

  const renderMultiLineChart = (title, data, series, reportTab) => (
    <Card className="border-0 mb-3" style={{ borderRadius: 14, boxShadow: "0 2px 12px rgba(0,0,0,.07)" }}>
      <Card.Body>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="fw-bold mb-0">{title}</h6>
          {reportTab && (
            <button
              type="button"
              className="btn btn-link btn-sm p-0 text-decoration-none"
              onClick={() => goReport(reportTab)}
            >
              Xem báo cáo <FaArrowRight size={11} />
            </button>
          )}
        </div>
        {data.length > 0 ? (
          <div className="acc-chart-wrap acc-chart-wrap-lg">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 5, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="bucket" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={fmtShort} tick={{ fontSize: 12 }} width={64} />
                <Tooltip content={<ChartTooltip />} />
                <Legend />
                {series.map((s) => (
                  <Line
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    stroke={s.color}
                    strokeWidth={s.dashed ? 2 : 2.5}
                    strokeDasharray={s.dashed ? "6 4" : undefined}
                    dot={{ r: s.dashed ? 2 : 3 }}
                    activeDot={{ r: 5 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-center text-muted py-5">Chưa có dữ liệu</div>
        )}
      </Card.Body>
    </Card>
  );

  const selectedLineName = useMemo(() => {
    if (!selectedLineId) return "";
    const found = lineReports.find((lr) => String(lr.lineId) === String(selectedLineId));
    return found?.lineName || `Line ${selectedLineId}`;
  }, [selectedLineId, lineReports]);

  const renderChartArea = () => {
    const t = chartMetric.type;

    if (t === "total" || t === "line") {
      const title = t === "line"
        ? `Doanh thu & Chi phí — ${selectedLineName} · ${periodLabel}`
        : `Doanh thu & Chi phí — ${periodLabel} vs kỳ trước`;
      return (
        <>
          {renderMultiLineChart(title, monthlyChartData, [
            { key: "Doanh thu", color: "#198754" },
            { key: "Chi phí", color: "#dc3545" },
            { key: "Doanh thu kỳ trước", color: "#198754", dashed: true },
            { key: "Chi phí kỳ trước", color: "#dc3545", dashed: true },
          ])}
          {t === "total" && (
            <Row className="g-3 mb-4">
              <Col xs={12} lg={6}>
                {renderPie("Doanh thu theo Line KD", revenuePie)}
              </Col>
              <Col xs={12} lg={6}>
                {renderPie("Chi phí theo hạng mục", categoryPie, (entry) => {
                  if (entry?.id != null) selectMetric({ type: "category", id: entry.id, name: entry.name });
                })}
              </Col>
            </Row>
          )}
        </>
      );
    }

    if (t === "assets") {
      return renderMultiLineChart("Biến động tài sản (toàn công ty)", assetChartData, [
        { key: "Tổng tài sản", color: "#20c997" },
        { key: "Tài sản lưu động", color: "#0d6efd" },
        { key: "Tài sản cố định", color: "#6f42c1" },
      ], "assets");
    }

    if (t === "cashflow") {
      return renderMultiLineChart(`Dòng tiền — ${periodLabel}`, cashflowChartData, [
        { key: "Tiền vào", color: "#198754" },
        { key: "Tiền ra", color: "#dc3545" },
      ], "cashflow");
    }

    if (t === "grossProfit" || t === "netProfit") {
      const data = t === "grossProfit" ? (profit?.grossTrend || []) : (profit?.netTrend || []);
      const label = t === "grossProfit" ? "Lợi nhuận gộp" : "Lợi nhuận ròng";
      return (
        <PeriodCompareLineChart
          data={data}
          title={`${label} — ${periodLabel} vs kỳ trước`}
          currentLabel="Kỳ này"
          previousLabel="Kỳ trước"
          currentColor="#0d6efd"
        />
      );
    }

    // revenue / partnerExpense / operatingExpense / category
    const titles = {
      revenue: "Doanh thu",
      partnerExpense: "Chi phí NCC",
      operatingExpense: "Chi phí vận hành",
      category: chartMetric.name || "Hạng mục chi",
    };
    if (scopedTrendLoading) {
      return (
        <Card className="border-0 mb-3" style={{ borderRadius: 14 }}>
          <Card.Body className="text-center py-5">
            <Spinner animation="border" size="sm" /> <span className="text-muted">Đang tải biểu đồ…</span>
          </Card.Body>
        </Card>
      );
    }
    return (
      <PeriodCompareLineChart
        data={scopedTrend}
        title={`${titles[t]} — ${periodLabel} vs kỳ trước`}
        currentLabel="Kỳ này"
        previousLabel="Kỳ trước"
        currentColor={t === "revenue" ? "#198754" : "#dc3545"}
      />
    );
  };

  return (
    <div className={embedded ? "acc-module" : "acc-page"}>
      <div
        className="rounded-3 p-3 p-md-4 mb-4 acc-dash-hero d-flex flex-wrap align-items-center justify-content-between gap-3"
        style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "#fff" }}
      >
        <div>
          <h4 className="fw-bold mb-1" style={{ letterSpacing: 0.3 }}>
            Dashboard điều hành tài chính
          </h4>
          <span style={{ opacity: 0.85 }} className="acc-kpi-meta">
            Tổng hợp doanh thu, chi phí, lợi nhuận, tài sản và dòng tiền
            {selectedLineId ? ` · Lọc: ${selectedLineName}` : ""}
          </span>
        </div>
        <div className="d-flex flex-wrap gap-2 align-items-center acc-dash-hero-controls">
          <Form.Select
            size="sm"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            style={{ width: 150, borderRadius: 8 }}
            className="text-dark"
          >
            {PERIOD_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Form.Select>
          {period === "CUSTOM" && (
            <>
              <Form.Control
                type="date"
                size="sm"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                style={{ width: 150, borderRadius: 8 }}
                className="text-dark"
              />
              <Form.Control
                type="date"
                size="sm"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                style={{ width: 150, borderRadius: 8 }}
                className="text-dark"
              />
            </>
          )}
          <Form.Select
            size="sm"
            value={selectedLineId || ""}
            onChange={handleLineFilterChange}
            style={{ width: 220, borderRadius: 8 }}
            className="text-dark"
            aria-label="Lọc theo Line kinh doanh"
          >
            <option value="">Tất cả Line KD</option>
            {lineReports
              .filter((line) => line.lineId != null)
              .map((line) => (
                <option key={line.lineId} value={line.lineId}>
                  {line.lineName || `Line ${line.lineId}`}
                </option>
              ))}
          </Form.Select>
        </div>
      </div>

      {loading && !summary ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <div className="mt-2 text-muted">Đang tải dữ liệu…</div>
        </div>
      ) : (
        <>
          <div className="acc-summary-grid--equal" style={{ opacity: loading ? 0.6 : 1 }}>
            {kpis.map((k) => (
              <div className="acc-summary-card-wrap" key={k.key}>
                <ReportSummaryCard
                  title={k.title}
                  icon={k.icon}
                  color={k.color}
                  value={k.value}
                  format={k.format || "money"}
                  loading={loading}
                  compact
                  subtitle={k.subtitle}
                  delta={k.delta}
                  deltaLabel="vs kỳ trước"
                  invertDelta={k.invertDelta}
                  onClick={() => selectMetric({ type: k.metric || k.key })}
                  active={isMetricActive(k.key)}
                />
              </div>
            ))}
          </div>

          {renderChartArea()}

          <Row className="g-3 mb-4">
            <Col xs={12} lg={4}>
              <TopListCard
                title="Top Line kinh doanh"
                icon={FaLayerGroup}
                iconColor="#6f42c1"
                rows={[...lineReports]
                  .filter((lr) => Number(lr.revenue || 0) > 0)
                  .sort((a, b) => Number(b.revenue || 0) - Number(a.revenue || 0))
                  .slice(0, 5)
                  .map((lr) => ({ id: lr.lineId, name: lr.lineName, totalAmount: Number(lr.revenue || 0) }))}
                emptyText="Chưa có doanh thu theo line trong kỳ"
                onRowClick={(r) => selectMetric({ type: "line", id: r.id, name: r.name })}
                activeId={selectedLineId}
                footerLink={() => goReport("revenue")}
              />
            </Col>
            <Col xs={12} lg={4}>
              <TopListCard
                title="Top Khách hàng"
                icon={FaUsers}
                iconColor="#198754"
                rows={topCustomers}
                emptyText="Chưa có doanh thu theo khách hàng trong kỳ"
                footerLink={() => goReport("revenue")}
              />
            </Col>
            <Col xs={12} lg={4}>
              <TopListCard
                title="Top Hạng mục chi phí"
                icon={FaTags}
                iconColor="#fd7e14"
                rows={topCategories}
                emptyText="Chưa có chi phí trong kỳ"
                onRowClick={(r) => selectMetric({ type: "category", id: r.id, name: r.name })}
                activeId={chartMetric.type === "category" ? chartMetric.id : null}
                footerLink={() => goReport("expense")}
              />
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col xs={12} lg={4}>
              <TopListCard
                title="Top Đối tác theo chi phí"
                icon={FaTruck}
                iconColor="#dc3545"
                rows={topPartners}
                emptyText="Chưa có chi phí đối tác trong kỳ"
                footerLink={() => goReport("expense")}
              />
            </Col>

            <Col xs={12} lg={4}>
              <Card className="border-0 h-100" style={{ borderRadius: 14, boxShadow: "0 2px 12px rgba(0,0,0,.07)" }}>
                <Card.Body>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="fw-bold mb-0">Tổng hợp công nợ</h6>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-decoration-none"
                      onClick={() => navigate("/ke-toan/cong-no")}
                    >
                      Xem <FaArrowRight size={11} />
                    </button>
                  </div>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-1 acc-kpi-meta">
                      <span>Phải thu</span>
                      <span className="fw-bold" style={{ color: "#0d6efd" }}>{formatVND(summary?.totalReceivable)}</span>
                    </div>
                    <ProgressBar now={receivablePct} variant="primary" style={{ height: 8, borderRadius: 4 }} />
                  </div>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-1 acc-kpi-meta">
                      <span>Phải trả</span>
                      <span className="fw-bold" style={{ color: "#dc3545" }}>{formatVND(summary?.totalPayable)}</span>
                    </div>
                    <ProgressBar now={100 - receivablePct} variant="danger" style={{ height: 8, borderRadius: 4 }} />
                  </div>
                  <hr />
                  <div className="d-flex justify-content-between">
                    <span className="fw-bold">Chênh lệch</span>
                    <span
                      className="fw-bold"
                      style={{
                        color: (summary?.totalReceivable || 0) - (summary?.totalPayable || 0) >= 0 ? "#198754" : "#dc3545",
                      }}
                    >
                      {formatVND((summary?.totalReceivable || 0) - (summary?.totalPayable || 0))}
                    </span>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} lg={4}>
              <Card className="border-0 h-100" style={{ borderRadius: 14, boxShadow: "0 2px 12px rgba(0,0,0,.07)" }}>
                <Card.Body>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h6 className="fw-bold mb-0">
                      <FaUniversity className="me-2" style={{ color: "#6f42c1" }} />
                      Tài khoản ngân hàng
                    </h6>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-decoration-none"
                      onClick={() => goReport("cashflow")}
                    >
                      Dòng tiền <FaArrowRight size={11} />
                    </button>
                  </div>
                  {bankAccounts.length > 0 ? (
                    <div className="acc-table-wrap">
                      <table className="table table-sm table-hover mb-0">
                        <thead>
                          <tr className="text-muted">
                            <th>Ngân hàng</th>
                            <th className="acc-col-hide-sm">Số TK</th>
                            <th className="text-end">Số dư</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bankAccounts.map((acc) => (
                            <tr key={acc.id}>
                              <td className="fw-medium">{acc.bankName}</td>
                              <td className="text-muted acc-col-hide-sm">{acc.accountNumber}</td>
                              <td className="text-end fw-bold" style={{ color: "#198754" }}>{formatVND(acc.balance)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-top">
                            <td className="fw-bold">Tổng</td>
                            <td className="acc-col-hide-sm" />
                            <td className="text-end fw-bold" style={{ color: "#0d6efd" }}>
                              {formatVND(bankAccounts.reduce((s, a) => s + (a.balance || 0), 0))}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center text-muted py-4">Chưa có tài khoản ngân hàng</div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
};

const FinancialDashboard = () => (
  <AdLayout>
    <FinancialDashboardBody />
  </AdLayout>
);

export { FinancialDashboardBody };
export default FinancialDashboard;
